<script>
	import { onMount, onDestroy } from 'svelte';
	import { getApiUrl, getDivisionTitle, injectGlobalStyles, loadJson, normalizeCollection, normalizeText, teamUrl } from './shared.js';
	import WidgetSelect from './WidgetSelect.svelte';

	export let projectId = '';
	export let tag = 'mlbl';

	const LEAGUE_CHANGED_EVENT = 'ilb:league-changed';

	let host;
	let leagues = [];
	let selectedLeagueId = '';
	let selectedLeagueTag = '';
	let teams = [];
	let loadingLeagues = false;
	let loadingTeams = false;
	let errorMessage = '';
	let syncingFromGlobal = false;

	$: leagueOptions = buildLeagueOptions(leagues);

	$: if (selectedLeagueId && !loadingLeagues) {
		rememberLeagueId(selectedLeagueId);
		if (!syncingFromGlobal) {
			publishLeagueSelection(selectedLeagueId);
		} else {
			syncingFromGlobal = false;
		}
		loadLeagueTeams(selectedLeagueId);
	}


	function getLeagueSelectionStore() {
		const globalScope = window;
		if (!globalScope.__ilbSelectedLeagueByProject) {
			globalScope.__ilbSelectedLeagueByProject = {};
		}
		return globalScope.__ilbSelectedLeagueByProject;
	}

	function getStoredLeagueId() {
		if (!projectId) return '';
		return getLeagueSelectionStore()[projectId] || '';
	}

	function rememberLeagueId(leagueId) {
		if (!projectId || !leagueId) return;
		getLeagueSelectionStore()[projectId] = leagueId;
	}

	function publishLeagueSelection(leagueId) {
		if (!projectId || !leagueId) return;
		window.dispatchEvent(new CustomEvent(LEAGUE_CHANGED_EVENT, { detail: { projectId, leagueId } }));
	}

	function handleGlobalLeagueChange(event) {
		const payload = event?.detail;
		if (!payload || payload.projectId !== projectId) return;
		if (!payload.leagueId || payload.leagueId === selectedLeagueId) return;
		if (!leagueOptions.some((item) => item.value === payload.leagueId)) return;
		syncingFromGlobal = true;
		selectedLeagueId = payload.leagueId;
	}

	function getLeagueId(league, index) {
		return league?.id || league?._id || league?.providerId || `league-${index}`;
	}

	function getLeagueTag(league) {
		return league?.tag || league?.Tag || league?.id || league?.Id || '';
	}

	function buildLeagueOptions(items) {
		return (items || []).map((league, index) => ({
			value: getLeagueId(league, index),
			label: getDivisionTitle(league, `Лига ${index + 1}`)
		}));
	}

	function getSelectedLeague() {
		return leagues.find((league, index) => String(getLeagueId(league, index)) === String(selectedLeagueId)) || null;
	}

	function getTeam(item) {
		return item?.team || item?.Team || {};
	}

	function getTeamId(item) {
		const team = getTeam(item);
		return team?.id || team?.Id || null;
	}

	function getTeamName(item) {
		const team = getTeam(item);
		return normalizeText(team?.name || team?.Name || team?.shortName || team?.ShortName, 'Команда');
	}

	function getTeamLogo(item) {
		const team = getTeam(item);
		return team?.logo || team?.Logo || '';
	}

	function getSelectTitle() {
		const league = getSelectedLeague();
		const leagueTitle = getDivisionTitle(league, 'Лига');
		return `Клубы ${leagueTitle} (${teams.length})`;
	}

	async function loadLeagues() {
		if (!projectId) {
			leagues = [];
			selectedLeagueId = '';
			selectedLeagueTag = '';
			teams = [];
			return;
		}

		loadingLeagues = true;
		errorMessage = '';

		try {
			const url = `${getApiUrl()}/stats/project/${encodeURIComponent(projectId)}/leagues`;
			leagues = normalizeCollection(await loadJson(url));
			const options = buildLeagueOptions(leagues);
			const storedLeagueId = getStoredLeagueId();

			if (storedLeagueId && options.some((item) => item.value === storedLeagueId)) {
				selectedLeagueId = storedLeagueId;
			} else {
				selectedLeagueId = options[0]?.value || '';
			}
		} catch (error) {
			console.error('Load clubs leagues error:', error);
			errorMessage = 'Ошибка загрузки лиг';
			leagues = [];
			selectedLeagueId = '';
			selectedLeagueTag = '';
			teams = [];
		} finally {
			loadingLeagues = false;
		}
	}

	async function loadLeagueTeams(leagueId) {
		if (!leagueId) {
			teams = [];
			selectedLeagueTag = '';
			return;
		}

		const league = getSelectedLeague();
		const leagueTag = getLeagueTag(league) || tag;
		selectedLeagueTag = leagueTag;

		loadingTeams = true;
		errorMessage = '';

		try {
			const url = `${getApiUrl()}/ib/competition/${encodeURIComponent(leagueTag)}/team-stats`;
			teams = normalizeCollection(await loadJson(url));
		} catch (error) {
			console.error('Load clubs teams error:', error);
			errorMessage = 'Ошибка загрузки команд';
			teams = [];
		} finally {
			loadingTeams = false;
		}
	}


	function goToTeam(teamItem) {
		const teamId = getTeamId(teamItem);
		if (!teamId) return;
		window.location.href = teamUrl(teamId, { tag: selectedLeagueTag || tag, compId: selectedLeagueId });
	}

	function handleTeamKeyPress(event, teamItem) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			goToTeam(teamItem);
		}
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		window.addEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
		loadLeagues();
	});

	onDestroy(() => {
		window.removeEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
	});
</script>

<section class="section clubs" bind:this={host} data-project-id={projectId}>
	<div class="container">
		{#if loadingLeagues}
			<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
		{:else}
			<WidgetSelect
				name="clubs-league"
				className="--unstyled"
				options={leagueOptions.map((option) => ({ ...option, label: option.value === selectedLeagueId ? getSelectTitle() : `Клубы ${option.label}` }))}
				bind:value={selectedLeagueId}
				disabled={loadingLeagues || loadingTeams || !leagueOptions.length}
			/>
		{/if}

		<div class="filter__wrap">
			{#if errorMessage}
				<div class="widget-status" style="color: red;">{errorMessage}</div>
			{:else if loadingTeams}
				<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
			{:else if !teams.length}
				<div class="widget-status">Команды не найдены</div>
			{:else}
				<div class="clubs__grid" data-filter-content={getSelectTitle()}>
					{#each teams as teamItem}
						<div class="clubs__item" on:click={() => goToTeam(teamItem)} on:keypress={(event) => handleTeamKeyPress(event, teamItem)} role="button" tabindex="0" title="Открыть страницу команды">
							<div class="clubs__logo">
								{#if getTeamLogo(teamItem)}
									<img src={getTeamLogo(teamItem)} alt={getTeamName(teamItem)} loading="lazy">
								{/if}
							</div>
							<p class="clubs__name">{getTeamName(teamItem)}</p>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	</div>
</section>

<style>
	:host {
		display: block;
	}

	.clubs__grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
		gap: 16px;
	}

	.clubs__item {
		cursor: pointer;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 154px;
		gap: 8px;
		padding: 24px 8px;
		border-radius: 8px;
		background-color: rgba(255, 255, 255, .1);
	}

	.clubs__logo {
		width: 64px;
		height: 64px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.clubs__logo img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	.clubs__name {
		margin: 0;
		text-align: center;
	}

	@media screen and (max-width: 700px) {
		.clubs__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
</style>
